/**
 * Design V2 Configuration
 *
 * Manages mode (light/dim/dark) and brand (immotron/unisun/uniplacement)
 * for the v2 design system. Tokens are applied via data-attributes on <html>.
 */

// ── Types ─────────────────────────────────────────────────

export type V2Mode = 'light' | 'dim' | 'dark'
export type V2Brand = 'immotron' | 'unisun' | 'uniplacement'

const VALID_MODES: V2Mode[] = ['light', 'dim', 'dark']
const VALID_BRANDS: V2Brand[] = ['immotron', 'unisun', 'uniplacement']

const STORAGE_KEY_MODE = 'v2-mode'
const STORAGE_KEY_FORCE_V1 = 'v2-force-v1'

export const DEFAULT_MODE: V2Mode = 'dim'
export const DEFAULT_BRAND: V2Brand = 'immotron'

// ── Brand Detection ───────────────────────────────────────

/**
 * Known UID-prefix → Brand mapping.
 * Swiss UIDs follow CHE-XXX.XXX.XXX format.
 * Each company/mandant has a UID; this maps it to the correct brand.
 */
const BRAND_MAP: Record<string, V2Brand> = {
  // Unisun AG
  'CHE-248.729.498': 'unisun',
  // Uniplacement AG
  'CHE-115.694.498': 'uniplacement',
}

/**
 * Determine brand from a company UID string.
 * Falls back to 'immotron' if UID is unknown or empty.
 */
export function getBrandFromUID(uid: string | null | undefined): V2Brand {
  if (!uid) return DEFAULT_BRAND
  const normalized = uid.trim()
  for (const [prefix, brand] of Object.entries(BRAND_MAP)) {
    if (normalized.startsWith(prefix)) return brand
  }
  return DEFAULT_BRAND
}

// ── Mode Persistence ──────────────────────────────────────

/**
 * Read the persisted mode from localStorage.
 * Returns DEFAULT_MODE if nothing is stored or value is invalid.
 */
export function getPersistedMode(): V2Mode {
  try {
    const stored = localStorage.getItem(STORAGE_KEY_MODE)
    if (stored && VALID_MODES.includes(stored as V2Mode)) {
      return stored as V2Mode
    }
  } catch {
    // localStorage unavailable (SSR, private browsing)
  }
  return DEFAULT_MODE
}

/**
 * Persist mode to localStorage and apply it to <html> with smooth transition.
 */
export function persistMode(mode: V2Mode): void {
  if (!VALID_MODES.includes(mode)) return
  try {
    localStorage.setItem(STORAGE_KEY_MODE, mode)
  } catch {
    // localStorage unavailable
  }
  // Enable smooth color transition during mode switch
  document.documentElement.classList.add('v2-transitioning')
  applyMode(mode)
  setTimeout(() => {
    document.documentElement.classList.remove('v2-transitioning')
  }, 350)
}

// ── Force V1 ──────────────────────────────────────────────

/**
 * Check if the user has opted to stay on the v1 design.
 * This is a local override — if true, the v2 layout is not rendered.
 */
export function isForceV1(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY_FORCE_V1) === 'true'
  } catch {
    return false
  }
}

/**
 * Set or clear the force-v1 flag.
 */
export function setForceV1(force: boolean): void {
  try {
    if (force) {
      localStorage.setItem(STORAGE_KEY_FORCE_V1, 'true')
    } else {
      localStorage.removeItem(STORAGE_KEY_FORCE_V1)
    }
  } catch {
    // localStorage unavailable
  }
}

// ── DOM Helpers ───────────────────────────────────────────

/**
 * Apply mode data-attribute to <html>.
 */
export function applyMode(mode: V2Mode): void {
  document.documentElement.setAttribute('data-v2-mode', mode)
}

/**
 * Apply brand data-attribute to <html>.
 */
export function applyBrand(brand: V2Brand): void {
  if (!VALID_BRANDS.includes(brand)) return
  document.documentElement.setAttribute('data-v2-brand', brand)
}

/**
 * Initialize v2 design system: apply persisted mode + brand.
 * Call this early (e.g. in main.tsx or a layout component).
 */
export function initDesignV2(brand?: V2Brand): void {
  applyMode(getPersistedMode())
  applyBrand(brand ?? DEFAULT_BRAND)
}
